import { afterNextRender, Component, ElementRef, EventEmitter, inject, Injector, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MarkdownComponent } from "ngx-markdown";

import { UchatSource } from "../../../model/uchat.model";
import { UChatFeedbackPayload } from "src/app/model/uchatFeedbackPayload";

@Component({
    selector: 'app-chat-turn',
    standalone: true,
    imports: [
        MarkdownComponent,
        FormsModule
    ],
    templateUrl: './chat-turn.component.html',
    styleUrl: './chat-turn.component.scss'
})
export class ChatTurnComponent implements OnChanges {

    // Per-instance suffix for the ids the ARIA attributes point at.
    private static nextUid = 0;
    private readonly uid = ++ChatTurnComponent.nextUid;

    private host = inject(ElementRef);
    private injector = inject(Injector);

    public readonly ratingLabelId = `turn-${this.uid}-rating-label`;
    public readonly commentInputId = `turn-${this.uid}-comment`;
    public readonly counterId = `turn-${this.uid}-comment-count`;
    public readonly errorId = `turn-${this.uid}-comment-error`;

    public isPositive = 1;
    public positiveRating = "up";
    public isNegative = -1;
    public negativeRating = "down";

    @Input() public turnRole: "user" | "assistant" = "assistant";

    @Input() public content: string = "";

    @Input() public sources: UchatSource[] = [];

    @Input() public streaming: boolean = false;

    @Input() public failed: boolean = false;

    @Input() public incomplete: boolean = false;

    @Input() public agentName: string | null = null;

    @Input() public rating: number | null = null;

    @Input() public turnId: string | null = null;

    // Set by the parent when saving this turn's rating fails.
    @Input() public feedbackError: string = "";

    // Set by the parent while this turn's rating is being saved.
    @Input() public feedbackSending: boolean = false;

    // Tints the speaker label; only the welcome turn sets it. 
    @Input() public accent: "it" | "student" | null = null;

    @Output() public feedbackEmitter = new EventEmitter<UChatFeedbackPayload>();

    public toSubmitRatingScore:number|null = null;

    public maxFeedbackLength:number = 1000;

    public feedbackInput:string = "";

    /**
     * The display name for the welcome turn's "Hi <name>!" line.
     */
    @Input() public greetingName: string | null = null;

    public get getSources(): UchatSource[] {
        let seenUrls = new Set<string>();
        return this.sources.filter((source:UchatSource)=> {
            if(!source.url){
                return false;
            }
            if(seenUrls.has(source.url)){
                return false;
            } 
            seenUrls.add(source.url);
            return true;
        });

    }

    public ngOnChanges(changes: SimpleChanges): void {
        let change = changes["rating"];
        // The focused rating controls are about to be replaced; hand focus to the saved rating.
        if ( change != null && change.previousValue == null && change.currentValue != null && this.toSubmitRatingScore != null ) {
            afterNextRender(() => {
                this.host.nativeElement.querySelector(".saved-rating")?.focus();
            }, { injector: this.injector });
        }
    }

    public get commentDescribedBy(): string {
        return this.feedbackError !== "" ? `${this.counterId} ${this.errorId}` : this.counterId;
    }

    public setRatingScore(newNumber:number){
        this.toSubmitRatingScore = newNumber;
    }

    public get canSubmitRating():boolean{
        return this.rating == null && this.toSubmitRatingScore !=null;
    }

    public sendFeedback(){
        if ( !this.canSubmitRating || this.isSoftLimitExceeded || this.feedbackSending ) {
            return;
        }
        let toSubmit:UChatFeedbackPayload = {
            turnId: this.turnId,
            rating: this.convertRatingNumberToRatingString(this.toSubmitRatingScore),
            comment: this.feedbackInput
        }
        this.feedbackEmitter.emit(toSubmit)
    }

    get feedbackInputLength():number{
        return this.feedbackInput.length;
    }

    public get isSoftLimitExceeded():boolean{
        return this.feedbackInputLength > this.maxFeedbackLength;
    }

    public convertRatingNumberToRatingString(toConvert:number):string {
        let converted:string = "";
        if(toConvert == this.isNegative){
            converted = this.negativeRating;
        }
        else if(toConvert == this.isPositive){
            converted = this.positiveRating;
        }
        return converted;
    }
}
